import type { SearchFormCardProps } from "@/pages/carrier-cargo-list/ui/SearchForms";
import { DateRangePicker } from "@/shared/ui/DateRangePicker";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import { PrefectureField } from "@/widgets/prefecture-field";
import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import type { MouseEvent } from "react";
import { useForm } from "react-hook-form";

import {
  OFFER_CARGO_VEHICLE_SHAPES_LIST,
  PACKING_STYLES_LIST,
  isOfferCargoVehicleShape,
  isPackagingStyle,
} from "@/shared/constants/offer-cargo";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseCheckbox } from "@/shared/ui/base-checkbox";
import { BaseNumberField } from "@/shared/ui/base-number-field";
import { BaseSelect } from "@/shared/ui/base-select";
import {
  OfferCargoSearchFormSchema,
  type OfferCargoSearchFormSchemaType,
} from "../../SearchForms/schema";

type Props = {
  open: boolean;
  onClose: () => void;
} & SearchFormCardProps;

const LocationSelect = ({
  label,
  prefecture,
  onPrefectureChange,
}: {
  label: string;
  prefecture: string | undefined;
  onPrefectureChange: (prefecture: string | undefined) => void;
}) => (
  <>
    <BaseFormLabel label={`${label}(都道府県)`} sx={{ flex: 1 }}>
      <PrefectureField
        value={prefecture}
        size="small"
        onChange={(v) => onPrefectureChange(v)}
      />
    </BaseFormLabel>
  </>
);

export const MobileSearchForms = ({
  open,
  onClose,
  onGetOffersCargosParamsChange,
  onGetOffersCargosParamsReset,
  onIsNewFilterChange,
}: Props) => {
  const { handleSubmit, setValue, watch, reset } =
    useForm<OfferCargoSearchFormSchemaType>({
      resolver: zodResolver(OfferCargoSearchFormSchema),
    });
  const currentValue = watch();

  const handleSearchClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    handleSubmit((data) => {
      onGetOffersCargosParamsChange({
        loadingOnFrom: data.loadingScheduleStart,
        loadingOnTo: data.loadingScheduleEnd,
        loadingPrefecture: data.loadingLocationPrefecture,
        unloadingPrefecture: data.unloadingLocationPrefecture,
        packingStyle: data.packingStyle,
        vehicleShape: data.vehicleShape,
        shippingFee: data.shippingFee,
      });
      onIsNewFilterChange(data.isNew);
    })();

    // NOTE: 検索パラメーターをクエリで保持するようになったら閉じなくて良くなる
    onClose();
  };
  const handleResetClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onGetOffersCargosParamsReset();
    reset();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="bottom"
      sx={{ display: { xs: "block", sm: "none" } }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          height: "95vh",
        },
      }}
    >
      <Stack
        direction="column"
        sx={{
          borderRadius: "16px",
          flex: 1,
          overflowY: "hidden",
        }}
      >
        {/* ヘッダー部分 */}
        <Stack
          direction="row"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            height: "48px",
            flexShrink: 0,
          }}
        >
          <Typography fontSize={18} fontWeight={700}>
            フィルター
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              width: "48px",
              height: "48px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "primary.400",
            }}
          >
            <CloseIcon width={24} height={24} />
          </IconButton>
        </Stack>

        <Divider />

        {/* フォーム部分 */}
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          <Stack spacing={3} p={2} flex={1}>
            {/* 積日 */}
            <DateRangePicker
              label="積日"
              startDate={currentValue.loadingScheduleStart}
              endDate={currentValue.loadingScheduleEnd}
              onStartDateChange={(date) =>
                setValue("loadingScheduleStart", date)
              }
              onEndDateChange={(date) => setValue("loadingScheduleEnd", date)}
            />

            {/* 積地＆卸地 */}
            <Stack direction="row" spacing={2}>
              <LocationSelect
                label="積地"
                prefecture={currentValue.loadingLocationPrefecture}
                onPrefectureChange={(prefecture) =>
                  setValue("loadingLocationPrefecture", prefecture)
                }
              />

              <LocationSelect
                label="卸地"
                prefecture={currentValue.unloadingLocationPrefecture}
                onPrefectureChange={(prefecture) =>
                  setValue("unloadingLocationPrefecture", prefecture)
                }
              />
            </Stack>

            {/* 荷姿 */}
            <Box>
              <BaseFormLabel label={"荷姿"}>
                <BaseSelect
                  value={currentValue.packingStyle}
                  size="small"
                  type="string"
                  noSelectedOptionText="(選択解除)"
                  sx={{ minWidth: 256 }}
                  onChange={(v) => {
                    setValue(
                      "packingStyle",
                      isPackagingStyle(v) ? v : undefined,
                    );
                  }}
                >
                  {PACKING_STYLES_LIST.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </BaseSelect>
              </BaseFormLabel>
            </Box>

            {/* ボディタイプ */}
            <Box>
              <BaseFormLabel label={"ボディタイプ"}>
                <BaseSelect
                  value={currentValue.vehicleShape}
                  size="small"
                  type="string"
                  noSelectedOptionText="(選択解除)"
                  sx={{ minWidth: 256 }}
                  onChange={(v) => {
                    setValue(
                      "vehicleShape",
                      isOfferCargoVehicleShape(v) ? v : undefined,
                    );
                  }}
                >
                  {OFFER_CARGO_VEHICLE_SHAPES_LIST.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </BaseSelect>
              </BaseFormLabel>
            </Box>

            {/* 運賃 */}
            <Box>
              <BaseFormLabel label={"運賃"}>
                <BaseNumberField
                  value={currentValue.shippingFee}
                  size="small"
                  unit="円以上"
                  sx={{ minWidth: 128 }}
                  onChange={(value) => {
                    setValue("shippingFee", value === "" ? undefined : value);
                  }}
                />
              </BaseFormLabel>
            </Box>

            {/* 新着のみ・未提案のみ */}
            <Stack direction="row" spacing={2} py={1}>
              <BaseCheckbox
                size="small"
                label="新着のみ"
                value={currentValue.isNew ?? false}
                onChange={(v) => {
                  setValue("isNew", v);
                }}
              />
              <BaseCheckbox
                size="small"
                label="未提案のみ"
                value={currentValue.isNotProposedYet ?? false}
                onChange={(v) => {
                  setValue("isNotProposedYet", v);
                }}
              />
            </Stack>
          </Stack>
        </Box>

        <Divider sx={{ mt: 4 }} />

        {/* ボタン部分 */}
        <Stack direction="row" spacing={1.5} px={2} py={3} flexGrow={0}>
          <BaseButton
            size="large"
            variant="outlined"
            onClick={handleResetClick}
            sx={{ flex: 1, minWidth: "auto" }}
          >
            リセット
          </BaseButton>

          <BaseButton
            size="large"
            variant="contained"
            onClick={handleSearchClick}
            sx={{ flex: 1, minWidth: "auto" }}
          >
            絞り込む
          </BaseButton>
        </Stack>
      </Stack>
    </Drawer>
  );
};
